/**
 * Stage A — the interaction plan for one timer act.
 *
 * Declares, per intent, the HUMAN facts the act needs (in order), the server
 * authority that decides it, and the effects that may follow ONLY a confirmed
 * success. A surface renders whatever it likes for each input step; later
 * stages replace a step's UI (Start: C, Pause: D, Stop: E) without touching how
 * the act is executed.
 *
 * Deliberately absent: pre-repair authorization, bay occupancy, ticket
 * availability, timer topology and job-scope validity. The server decides all
 * of them and returns a typed refusal; the plan only knows the server CAN
 * refuse (see `serverMayRequire`).
 */
import { availableTimerActions, type TimerActionIntent, type TimerWorkState } from './workState';

/** A fact only a human can supply, collected before the server is asked. */
export type TimerInputStep =
  /** Today's acknowledgement gate before beginning or returning to work (policy). */
  | 'SERVICE_INFO'
  /** Which open jobs the time is for. A single open job answers itself. */
  | 'JOB_SCOPE'
  /** Where the work physically happens, when the vehicle is not in a work position (today: a bay, for a parked vehicle). */
  | 'WORK_TARGET'
  /**
   * Stage D: "Why are you stopping work?" — ONE answer (pauseIntent.ts), an
   * optional note, and optional findings for diagnostic jobs finished now.
   * The answer itself decides advisor / technical-help attention and whether
   * a whole-ticket FOH handoff is asked for; there is no second question.
   */
  | 'PAUSE_DETAILS'
  /**
   * Stage E: diagnostic findings for this technician's episode and — only when
   * they are the LAST live BOH worker and the vehicle is in a bay — where the
   * vehicle is left. No FOH question: the final-work topology answers it.
   */
  | 'STOP_DETAILS'
  /** Resuming would run past the technician's scheduled day: someone decides first. */
  | 'EOD_DECISION';

/** The server authority each act ends in. The RPCs stay specialised. */
export type TimerServerAction =
  | 'start_my_timer_with_scope'
  | 'pull_in_and_start_my_timer'
  | 'pause_my_work'                           // Stage D: the one answer → pause_my_timer (or its diagnostic-capture variant) + attention, one transaction
  | 'resume_my_timer'
  | 'finish_my_work'                          // Stage E: Stop (running) or End Paused Work, + final move + FOH handoff, one transaction
  | 'none';                                   // REVIEW_TIME opens the review; it mutates nothing here

/** Effects that may run only after the server confirmed success. */
export type TimerSuccessEffect =
  | 'REFRESH_MY_STATE'      // re-read this technician's own state
  | 'REFRESH_BOARD'         // tell the surface's parent the ticket changed
  | 'MARKETING_NOTICE'      // film-mode "timer started" notice
  | 'CUSTODY_ACCEPT'        // clear a pending_tech_accept kick-back once work is timed again
  | 'OPEN_REVIEW'           // offer the frozen allocation proposal
  | 'FOH_HANDOFF';          // Pause: the handoff an advisor answer asks for (server-gated). Stop: follow-up of the server's own handoff

/** Server-side reasons an act may come back needing someone else (never predicted here). */
export type TimerServerRequirement = 'PRE_REPAIR_AUTHORIZATION' | 'ALLOCATION_REVIEW' | 'RESUME_INSTEAD' | 'PULL_IN_TO_BAY';

/** Policy toggles for input steps. Today's behaviour is the default. */
export interface TimerInteractionPolicy {
  /** Ask for the service-information acknowledgement before Start. */
  serviceInfoBeforeStart: boolean;
  /** Ask for the service-information acknowledgement before Resume. */
  serviceInfoBeforeResume: boolean;
}

/**
 * Product decision: technicians are NOT asked the Service Info acknowledgement
 * on every Start and Resume. Start goes straight to job scope → Guided Start;
 * Resume goes straight to the server (EOD, pre-repair authorization and every
 * other server-authoritative refusal still apply exactly as before).
 *
 * The capability is RETAINED, not deleted: the `SERVICE_INFO` step, the
 * `ServiceInfoPrompt` UI, its OCIS acknowledgement event and history all stay,
 * and setting either flag back to `true` (or passing a policy to
 * `buildTimerActionPlan`) restores the step with no other change.
 */
export const CURRENT_TIMER_POLICY: TimerInteractionPolicy = {
  serviceInfoBeforeStart: false,
  serviceInfoBeforeResume: false,
};

export interface TimerPlanContext {
  state: TimerWorkState;
  /** The current location model says the vehicle is parked (not in a bay). */
  requiresPullIn: boolean;
  policy?: TimerInteractionPolicy;
}

export interface TimerActionPlan {
  intent: TimerActionIntent;
  allowed: boolean;
  /** Why not, when `allowed` is false. */
  blockedReason: string | null;
  /** Human facts to collect, in order. Empty ⇒ submit straight away. */
  steps: readonly TimerInputStep[];
  serverAction: TimerServerAction;
  /** What the server may answer instead of success (informational only). */
  serverMayRequire: readonly TimerServerRequirement[];
  /** Effects that run after — and only after — a confirmed success, in order. */
  successEffects: readonly TimerSuccessEffect[];
  /** Keep the collected inputs on screen when the server refuses (so the technician can correct them). */
  keepInputsOnRefusal: boolean;
}

export function buildTimerActionPlan(intent: TimerActionIntent, ctx: TimerPlanContext): TimerActionPlan {
  const policy = ctx.policy ?? CURRENT_TIMER_POLICY;
  const allowed = availableTimerActions(ctx.state).includes(intent);
  const base = {
    intent,
    allowed,
    blockedReason: allowed ? null : `${intent} is not available while ${ctx.state}`,
  };
  switch (intent) {
    case 'START':
      return {
        ...base,
        // Stage C — WORK_TARGET is Guided Start's "where does this work
        // happen?" step, run for every Start and resolved from what OptiBay
        // already knows (guidedStart.ts): a car already in a bay completes it
        // with no question; only a parked car shows the preview / countdown /
        // bay choice. It comes last, after the technician's scope.
        steps: [
          ...(policy.serviceInfoBeforeStart ? (['SERVICE_INFO'] as const) : []),
          'JOB_SCOPE',
          'WORK_TARGET',
        ],
        serverAction: ctx.requiresPullIn ? 'pull_in_and_start_my_timer' : 'start_my_timer_with_scope',
        serverMayRequire: ['PRE_REPAIR_AUTHORIZATION', 'ALLOCATION_REVIEW', 'RESUME_INSTEAD'],
        successEffects: ['REFRESH_MY_STATE', 'REFRESH_BOARD', 'MARKETING_NOTICE', 'CUSTODY_ACCEPT'],
        keepInputsOnRefusal: false,
      };
    case 'RESUME':
      return {
        ...base,
        steps: [
          ...(policy.serviceInfoBeforeResume ? (['SERVICE_INFO'] as const) : []),
          'EOD_DECISION',
        ],
        serverAction: 'resume_my_timer',
        // Oct 20 (D-4): a parked vehicle → the technician chooses a bay; the act is resubmitted with it.
        serverMayRequire: ['PRE_REPAIR_AUTHORIZATION', 'ALLOCATION_REVIEW', 'PULL_IN_TO_BAY'],
        successEffects: ['REFRESH_MY_STATE', 'REFRESH_BOARD', 'CUSTODY_ACCEPT'],
        keepInputsOnRefusal: false,
      };
    case 'PAUSE':
      return {
        ...base,
        steps: ['PAUSE_DETAILS'],
        serverAction: 'pause_my_work',
        serverMayRequire: [],
        successEffects: ['REFRESH_MY_STATE', 'REFRESH_BOARD', 'FOH_HANDOFF'],
        keepInputsOnRefusal: true,
      };
    case 'STOP':
      return {
        ...base,
        steps: ['STOP_DETAILS'],
        serverAction: 'finish_my_work',
        serverMayRequire: [],
        successEffects: ['REFRESH_MY_STATE', 'REFRESH_BOARD', 'OPEN_REVIEW', 'FOH_HANDOFF'],
        keepInputsOnRefusal: false,
      };
    case 'END_WORK':
      return {
        ...base,
        // Same dialog as Stop today; a paused technician has no open interval,
        // so the server offers no diagnostic capture here.
        steps: ['STOP_DETAILS'],
        serverAction: 'finish_my_work',
        serverMayRequire: [],
        successEffects: ['REFRESH_MY_STATE', 'REFRESH_BOARD', 'OPEN_REVIEW', 'FOH_HANDOFF'],
        keepInputsOnRefusal: false,
      };
    case 'REVIEW_TIME':
      return {
        ...base,
        steps: [],
        serverAction: 'none',
        serverMayRequire: [],
        successEffects: [],
        keepInputsOnRefusal: false,
      };
  }
}
